import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Label } from '@apps/web/components/ui/label';
import { Textarea } from '@apps/web/components/ui/textarea';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import {
    extractStreetName,
    formatFullAddress,
    substituteTemplateVariables,
    trimPatientNameAnnotations,
} from '@apps/web/lib/outreach_utils';
import { withTemplateVariableAliases } from '@apps/web/lib/template_variables';
import {
    useCompletionTemplates,
    useOutreachTemplates,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type JoinedOutreachRead } from '@libraries/typescript/api_client';

type RemovalReason =
    | 'cancelled'
    | 'filled'
    | 'filled-externally'
    | 'no_longer_available';

// Default item template matching backend DEFAULT_MULTI_SHIFT_ITEM_TEMPLATE
const DEFAULT_ITEM_TEMPLATE =
    '{shift_date}, {shift_start_time}-{shift_end_time}{location_text}';

/**
 * Format a shift list for the notification message preview.
 * This mirrors the backend logic in shifts_cancelled_notify.py
 */
function formatShiftList(
    shifts: Array<{
        shift_start_time: string;
        shift_end_time: string;
        patient_name?: string | null;
        patient_city?: string | null;
        patient_state?: string | null;
        patient_postal_code?: string | null;
        patient_address?: string | null;
    }>,
    timezone: string,
    itemTemplate?: string | null,
): string {
    const template = itemTemplate || DEFAULT_ITEM_TEMPLATE;

    const lines = shifts.map((shift) => {
        const startDate = new Date(shift.shift_start_time);
        const endDate = new Date(shift.shift_end_time);

        const shiftDate = startDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: timezone,
        });
        const startTime = startDate
            .toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: timezone,
            })
            .replace(/^0/, '');
        const endTime = endDate
            .toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: timezone,
            })
            .replace(/^0/, '');

        const shiftDayOfWeek = startDate.toLocaleDateString('en-US', {
            weekday: 'long',
            timeZone: timezone,
        });

        const clientName = trimPatientNameAnnotations(shift.patient_name ?? '');
        const clientNameParts = clientName.split(/\s+/);
        const clientCity = shift.patient_city ?? '';
        const clientZipCode = shift.patient_postal_code ?? '';
        const clientAddress = shift.patient_address ?? '';
        const values = withTemplateVariableAliases({
            shift_date: shiftDate,
            shift_day_of_week: shiftDayOfWeek,
            shift_start_time: startTime,
            shift_end_time: endTime,
            client_first_name: clientNameParts[0] ?? '',
            client_last_name: clientNameParts.slice(1).join(' '),
            client_city: clientCity,
            client_zip_code: clientZipCode,
            client_address: formatFullAddress(
                clientAddress,
                clientCity,
                shift.patient_state,
                clientZipCode,
            ),
            client_street_name: extractStreetName(clientAddress),
            location_text: clientCity ? ` in ${clientCity}` : '',
        });

        return `- ${substituteTemplateVariables(template, values)}`;
    });

    return '\n' + lines.join('\n');
}

// Default completion templates (match backend defaults)
const DEFAULT_COMPLETION_TEMPLATES: Record<string, string> = {
    completion_cancelled:
        'Hi {caregiver_first_name}, the following shifts have been cancelled: {shift_list}',
    completion_filled:
        'Hi {caregiver_first_name}, the following shifts have been filled: {shift_list}',
    completion_no_longer_available:
        'Hi {caregiver_first_name}, the following shifts are no longer available: {shift_list}',
};

function isClockReminderOutreach(outreach: JoinedOutreachRead): boolean {
    return (
        outreach.outreach_type === 'clock_in' ||
        outreach.outreach_type === 'clock_out'
    );
}

type CancelOutreachDialogProps = {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly outreach: JoinedOutreachRead | null;
    readonly outreaches?: ReadonlyArray<JoinedOutreachRead>;
    readonly organizationId: string | null;
    readonly timezone: string;
    readonly onSuccess?: (result?: {
        readonly archivedOutreachIds?: ReadonlyArray<string>;
    }) => void;
    readonly organizationName?: string;
};

export function CancelOutreachDialog({
    open,
    onOpenChange,
    outreach,
    outreaches,
    organizationId,
    timezone,
    onSuccess,
    organizationName,
}: CancelOutreachDialogProps) {
    const [isCancelling, setIsCancelling] = useState(false);

    // Cancellation reason
    const [cancellationReason, setCancellationReason] = useState('');

    // Fetch completion templates from OrganizationTemplate
    const { data: completionTemplates } = useCompletionTemplates(
        organizationId,
        { reason: 'CancelOutreachDialog' },
    );

    // Fetch outreach templates to get the item template
    const { data: outreachTemplates } = useOutreachTemplates(organizationId, {
        reason: 'CancelOutreachDialog',
    });
    // Get the item template from the open-shift outreach template.
    const itemTemplate = useMemo(() => {
        if (!outreachTemplates) {
            return null;
        }
        const outreachTemplate = outreachTemplates.find(
            (t) => t.outreach_type === 'callout',
        );
        if (!outreachTemplate?.steps) {
            return null;
        }
        const smsStep = outreachTemplate.steps.find(
            (s) => s.step_type === 'sms',
        );
        return smsStep?.multi_shift_item_template ?? null;
    }, [outreachTemplates]);

    // Build templates map with fallback to defaults
    const templatesMap = useMemo(() => {
        const map: Record<string, string> = { ...DEFAULT_COMPLETION_TEMPLATES };
        if (completionTemplates) {
            for (const t of completionTemplates) {
                map[t.type] = t.template;
            }
        }
        return map;
    }, [completionTemplates]);

    // Archive and notification options
    const [sendNotifications, setSendNotifications] = useState(false);
    const [removalReason, setRemovalReason] =
        useState<RemovalReason>('cancelled');
    const [includeNonResponders, setIncludeNonResponders] = useState(false);

    // Reset state when dialog opens to prevent stale data from previous outreaches
    useEffect(() => {
        if (open) {
            setCancellationReason('');
            setSendNotifications(false);
            setRemovalReason('cancelled');
            setIncludeNonResponders(false);
        }
    }, [open]);

    const targetOutreaches = useMemo(() => {
        if (outreaches && outreaches.length > 0) {
            return [...outreaches];
        }
        return outreach ? [outreach] : [];
    }, [outreach, outreaches]);
    const primaryOutreach = targetOutreaches[0] ?? null;
    const isBulkArchive = targetOutreaches.length > 1;
    const isClockReminder =
        targetOutreaches.length === 1 &&
        !!primaryOutreach &&
        isClockReminderOutreach(primaryOutreach);
    const activeActionLabel = isClockReminder ? 'cancel' : 'archive';
    const activeActionLabelTitle = isClockReminder ? 'Cancel' : 'Archive';
    const activeActionProgressLabel = isClockReminder
        ? 'Canceling'
        : 'Archiving';
    const activeTargetLabel = isClockReminder
        ? 'clock reminder outreach'
        : 'outreach';

    // Generate message preview based on the selected removal reason
    const messagePreview = useMemo(() => {
        const previewShifts = targetOutreaches.flatMap(
            (target) => target.shifts,
        );
        if (!previewShifts.length) {
            return null;
        }

        // Get the appropriate template based on removal reason
        const templateKey =
            removalReason === 'filled-externally'
                ? 'completion_filled'
                : (`completion_${removalReason}` as keyof typeof DEFAULT_COMPLETION_TEMPLATES);
        const template =
            templatesMap[templateKey] ??
            DEFAULT_COMPLETION_TEMPLATES[templateKey];

        if (!template) {
            return null;
        }

        // Format the shift list using item template from outreach template
        const shiftList = formatShiftList(
            previewShifts,
            timezone,
            itemTemplate,
        );

        const values = withTemplateVariableAliases({
            caregiver_name: '{Caregiver Name}',
            caregiver_first_name: '{First Name}',
            caregiver_last_name: '{Last Name}',
            organization_name: organizationName || '{Organization}',
            shift_list: shiftList,
        });
        return substituteTemplateVariables(template, values);
    }, [
        templatesMap,
        targetOutreaches,
        removalReason,
        timezone,
        organizationName,
        itemTemplate,
    ]);

    const handleArchiveOutreach = async () => {
        if (targetOutreaches.length === 0 || !organizationId) {
            return;
        }

        setIsCancelling(true);

        const successfulActiveArchiveIds: Array<string> = [];
        const failedMessages: Array<string> = [];

        try {
            for (const targetOutreach of targetOutreaches) {
                const targetIsClockReminder =
                    isClockReminderOutreach(targetOutreach);
                const reason = targetIsClockReminder
                    ? null
                    : cancellationReason.trim() || null;
                const metadata = targetOutreach.outreach_metadata as {
                    filled_shifts?: Record<string, unknown>;
                    cancelled_shifts?: Array<string>;
                };
                const filledShiftIds = new Set(
                    Object.keys(metadata?.filled_shifts ?? {}),
                );
                const cancelledShiftIds = new Set(
                    metadata?.cancelled_shifts ?? [],
                );
                const pendingShiftIds = targetOutreach.shifts
                    .filter(
                        (s) =>
                            !filledShiftIds.has(s.id) &&
                            !cancelledShiftIds.has(s.id),
                    )
                    .map((s) => s.id);
                const shiftIdsToCancel = targetIsClockReminder
                    ? []
                    : pendingShiftIds;

                try {
                    const response = await getApiClient().post({
                        url: '/organizations/{organization_id}/callouts/{callout_id}/shifts/cancel',
                        pathParams: {
                            organization_id: organizationId,
                            callout_id: targetOutreach.id,
                        },
                        body: {
                            shift_ids: shiftIdsToCancel,
                            send_notifications:
                                !targetIsClockReminder && sendNotifications,
                            removal_reason: !targetIsClockReminder
                                ? removalReason
                                : null,
                            notification_reason: !targetIsClockReminder
                                ? sendNotifications
                                    ? removalReason
                                    : null
                                : null,
                            include_non_responders:
                                !targetIsClockReminder &&
                                sendNotifications &&
                                includeNonResponders,
                            cancellation_reason: reason,
                        },
                    });
                    const totalFailed =
                        response.total_failed ?? response.failures?.length ?? 0;
                    const archiveSucceeded =
                        response.outreach_auto_cancelled === true ||
                        (response.outreach_auto_cancelled === undefined &&
                            totalFailed === 0) ||
                        (targetIsClockReminder && totalFailed === 0);

                    if (archiveSucceeded) {
                        successfulActiveArchiveIds.push(targetOutreach.id);
                    } else {
                        failedMessages.push(
                            response.failures
                                ?.map(
                                    (failure: { readonly error?: string }) =>
                                        failure.error,
                                )
                                .filter(
                                    (message: string | undefined) =>
                                        message !== undefined,
                                )
                                .join(' ') || 'Archive did not complete.',
                        );
                    }
                } catch (error) {
                    const apiError = error as {
                        readonly body?: { readonly detail?: string };
                        readonly response?: {
                            readonly data?: { readonly detail?: string };
                        };
                    };
                    failedMessages.push(
                        apiError.body?.detail ??
                            apiError.response?.data?.detail ??
                            'Archive failed.',
                    );
                }
            }

            if (successfulActiveArchiveIds.length === 0) {
                toast.error(
                    isBulkArchive
                        ? 'Failed to archive selected outreaches'
                        : `Failed to ${activeActionLabel} outreach`,
                    {
                        description: failedMessages.join(' '),
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
                return;
            }

            if (isClockReminder) {
                toast.success('Clock reminder cancelled successfully', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
            } else if (sendNotifications) {
                if (
                    successfulActiveArchiveIds.length ===
                    targetOutreaches.length
                ) {
                    toast.success(
                        isBulkArchive
                            ? 'Outreaches archived. Notifying caregivers.'
                            : 'Outreach archived. Notifying caregivers.',
                        {
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                } else {
                    toast.warning(
                        `${successfulActiveArchiveIds.length} ${
                            successfulActiveArchiveIds.length === 1
                                ? 'outreach was'
                                : 'outreaches were'
                        } archived and notifications queued, but ${
                            targetOutreaches.length -
                            successfulActiveArchiveIds.length
                        } failed.`,
                        {
                            description: failedMessages.join(' '),
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                }
            } else {
                if (
                    successfulActiveArchiveIds.length ===
                    targetOutreaches.length
                ) {
                    toast.success(
                        isBulkArchive
                            ? 'Outreaches archived successfully'
                            : 'Outreach archived successfully',
                        {
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                } else {
                    toast.warning(
                        `${successfulActiveArchiveIds.length} ${
                            successfulActiveArchiveIds.length === 1
                                ? 'outreach was'
                                : 'outreaches were'
                        } archived, but ${
                            targetOutreaches.length -
                            successfulActiveArchiveIds.length
                        } failed.`,
                        {
                            description: failedMessages.join(' '),
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                }
            }
            onSuccess?.(
                successfulActiveArchiveIds.length > 0
                    ? { archivedOutreachIds: successfulActiveArchiveIds }
                    : undefined,
            );
            onOpenChange(false);
        } catch (error) {
            console.error('Error processing outreach:', error);
            if (error?.response?.status === 400) {
                toast.error(
                    error.response.data?.detail ||
                        `Cannot ${activeActionLabel} this outreach`,
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
            } else {
                toast.error(`Failed to ${activeActionLabel} outreach`, {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
            }
        } finally {
            setIsCancelling(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {isBulkArchive
                            ? `Archive ${targetOutreaches.length} Outreaches?`
                            : `${activeActionLabelTitle} ${isClockReminder ? 'Clock Reminder' : 'Outreach'}?`}
                    </DialogTitle>
                    <DialogDescription>
                        {isBulkArchive ? (
                            <React.Fragment>
                                Are you sure you want to archive{' '}
                                <strong>
                                    {targetOutreaches.length} outreaches
                                </strong>
                                ?
                                <br />
                                <br />
                                <strong className="text-red-600">
                                    Warning: These outreaches may have active
                                    communication in progress.
                                </strong>
                            </React.Fragment>
                        ) : (
                            primaryOutreach?.shifts[0] && (
                                <React.Fragment>
                                    Are you sure you want to {activeActionLabel}{' '}
                                    the {activeTargetLabel} for{' '}
                                    <strong>
                                        {primaryOutreach.shifts[0].patient_name}
                                    </strong>{' '}
                                    on{' '}
                                    {formatDateAndTime(
                                        primaryOutreach.shifts[0]
                                            .shift_start_time,
                                        timezone,
                                    )}
                                    ?
                                    <br />
                                    <br />
                                    <strong className="text-red-600">
                                        Warning: This outreach has active
                                        communication in progress.
                                    </strong>
                                </React.Fragment>
                            )
                        )}
                    </DialogDescription>
                </DialogHeader>
                {/* Notification options */}
                <div className="space-y-4 py-2">
                    {/* Cancellation reason */}
                    {!isClockReminder && (
                        <div className="space-y-2">
                            <Label htmlFor="cancellation-reason">
                                Reason for {activeActionLabel} (optional)
                            </Label>
                            <Textarea
                                id="cancellation-reason"
                                placeholder="Enter a reason for archiving this outreach…"
                                value={cancellationReason}
                                onChange={(e) =>
                                    setCancellationReason(e.target.value)
                                }
                                rows={3}
                            />
                        </div>
                    )}
                    {/* Notify checkbox */}
                    {!isClockReminder && (
                        <div className="flex items-start gap-3">
                            <Checkbox
                                id="send-notifications"
                                checked={sendNotifications}
                                onCheckedChange={(checked) =>
                                    setSendNotifications(checked === true)
                                }
                            />
                            <Label
                                htmlFor="send-notifications"
                                className="cursor-pointer text-sm font-medium"
                            >
                                Notify caregivers that the shifts are no longer
                                available
                            </Label>
                        </div>
                    )}
                    {/* Archive reason options */}
                    {!isClockReminder && (
                        <div className="space-y-3">
                            <Label className="text-muted-foreground text-xs font-medium">
                                Archive reason:
                            </Label>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        id="reason-cancelled"
                                        name="removal-reason"
                                        value="cancelled"
                                        checked={removalReason === 'cancelled'}
                                        onChange={() =>
                                            setRemovalReason('cancelled')
                                        }
                                        className="h-4 w-4"
                                    />
                                    <Label
                                        htmlFor="reason-cancelled"
                                        className="cursor-pointer text-sm font-normal"
                                    >
                                        Archived -{' '}
                                        <span className="text-muted-foreground">
                                            “shift has been cancelled”
                                        </span>
                                    </Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        id="reason-filled-externally"
                                        name="removal-reason"
                                        value="filled-externally"
                                        checked={
                                            removalReason ===
                                            'filled-externally'
                                        }
                                        onChange={() =>
                                            setRemovalReason(
                                                'filled-externally',
                                            )
                                        }
                                        className="h-4 w-4"
                                    />
                                    <Label
                                        htmlFor="reason-filled-externally"
                                        className="cursor-pointer text-sm font-normal"
                                    >
                                        Filled externally -{' '}
                                        <span className="text-muted-foreground">
                                            “shift has been filled”
                                        </span>
                                    </Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        id="reason-unavailable"
                                        name="removal-reason"
                                        value="no_longer_available"
                                        checked={
                                            removalReason ===
                                            'no_longer_available'
                                        }
                                        onChange={() =>
                                            setRemovalReason(
                                                'no_longer_available',
                                            )
                                        }
                                        className="h-4 w-4"
                                    />
                                    <Label
                                        htmlFor="reason-unavailable"
                                        className="cursor-pointer text-sm font-normal"
                                    >
                                        No longer available -{' '}
                                        <span className="text-muted-foreground">
                                            “shift is no longer available”
                                        </span>
                                    </Label>
                                </div>
                            </div>
                            {sendNotifications && (
                                <React.Fragment>
                                    {/* Message preview */}
                                    {messagePreview && (
                                        <div className="border-border bg-muted/50 mt-3 rounded border p-3">
                                            <p className="text-foreground/80 mb-2 text-xs font-medium">
                                                Message preview:
                                            </p>
                                            <p className="text-muted-foreground text-xs whitespace-pre-wrap italic">
                                                “{messagePreview}“
                                            </p>
                                        </div>
                                    )}
                                    <div className="mt-4 flex items-start gap-3">
                                        <Checkbox
                                            id="include-non-responders"
                                            checked={includeNonResponders}
                                            onCheckedChange={(checked) =>
                                                setIncludeNonResponders(
                                                    checked === true,
                                                )
                                            }
                                        />
                                        <Label
                                            htmlFor="include-non-responders"
                                            className="cursor-pointer text-sm font-normal"
                                        >
                                            Include caregivers who didn’t
                                            respond
                                        </Label>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isCancelling}
                    >
                        Close
                    </Button>
                    <Button
                        variant="default"
                        onClick={handleArchiveOutreach}
                        disabled={isCancelling}
                    >
                        {isCancelling
                            ? `${activeActionProgressLabel}…`
                            : isBulkArchive
                              ? 'Archive Outreaches'
                              : `${activeActionLabelTitle} Outreach`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
